import { app, MEASUREMENT_ID, USE_EMULATORS } from "./firebase";

// GA4 (via Firebase Analytics). Data shows up in the Firebase console and in the
// linked Google Analytics 4 property, so you can slice the funnel from a BI
// perspective: landing → download → sign-in → activate → top-up.
//
// The firebase/analytics SDK is code-split (dynamic import) and only pulled in
// when a measurement id is configured, so it adds nothing to the initial bundle
// in dev/preview or when analytics is off.
//
// Best-effort by design: no-ops without a measurement id, in the emulator, or
// where analytics isn't supported (private mode, no cookies, an ad-blocker).
// Analytics must never break the product — every call is guarded and swallowed.

let analytics: unknown = null;
let logEventFn: ((a: unknown, name: string, params?: Record<string, unknown>) => void) | null = null;
let setUserIdFn: ((a: unknown, id: string) => void) | null = null;

export async function initAnalytics(): Promise<void> {
  if (analytics || !MEASUREMENT_ID || USE_EMULATORS) return;
  try {
    const mod = await import("firebase/analytics");
    if (!(await mod.isSupported())) return;
    analytics = mod.getAnalytics(app);
    logEventFn = mod.logEvent as typeof logEventFn;
    setUserIdFn = mod.setUserId as typeof setUserIdFn;
  } catch (e) {
    console.warn("[analytics] disabled:", e);
  }
}

/** Log a GA4 event. Extra params become custom dimensions/metrics in GA4. */
export function track(name: string, params?: Record<string, unknown>): void {
  try {
    if (analytics && logEventFn) logEventFn(analytics, name, params);
  } catch {
    /* never surface analytics failures to the user */
  }
}

/** Associate subsequent events with a signed-in user (pseudonymous uid, no PII). */
export function identify(uid: string): void {
  try {
    if (analytics && setUserIdFn) setUserIdFn(analytics, uid);
  } catch {
    /* ignore */
  }
}
